import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isFeatureRunningClientSide, pricingUtils } from "@flights/web-api-utils-universal";
import { actions as searchAiSummaryActions } from "../../../../store/searchAiSummary/actions.js";
import { getSearch } from "../../../../store/search/selectors.js";
import { getSearchFetchStatus } from "../../../../store/searchResults/selectors.js";
import { getSearchFetchStatus as getSearchOutboundFetchStatus } from "../../../../store/searchResultsBounds/outboundSelectors.js";
import { getSearchFetchStatus as getSearchInboundFetchStatus } from "../../../../store/searchResultsBounds/inboundSelectors.js";
import useGetSearchResultsBound from "../../../../hooks/useGetSearchResultsBound.js";
import { clientFetch } from "@flights/client-fetch";
import useContextName from "../../../../hooks/useContextName.js";
import { useGetAvailableFilters } from "./boundsFilterHooks.js";
import useProfiler from "../../../../hooks/useProfiler.js";
const useAiSummaryController = (endpointVersion) => {
    const contextName = useContextName();
    const dispatch = useDispatch();
    const searchFetchStatus = useSearchFetchStatus();
    const { stops = [], airlines = [], summary } = useGetAvailableFilters();
    const { type, searchSegments, cabinClass, adults, children } = useSelector(getSearch);
    const { isLongHaul, isMediumHaul, isShortHaul } = useProfiler();
    const requestPayloadV2 = useMemo(() => {
        const payload = {
            passengers: adults + children.length,
            stops: stops.map(({ numberOfStops, count, minPrice }) => {
                return {
                    numberOfStops,
                    count,
                    minPrice: pricingUtils.priceToNumber(minPrice)
                };
            }),
            currencyCode: stops[0]?.minPrice.currencyCode || ""
        };
        return payload;
    }, [adults, children, stops]);
    const requestPayloadV3 = useMemo(() => {
        const payload = Object.assign({}, requestPayloadV2);
        payload.search = {
            type,
            adults,
            children,
            cabinClass,
            segments: searchSegments.map(({ from, to }) => {
                return {
                    from: from.map(({ code }) => code),
                    to: to.map(({ code }) => code)
                };
            })
        };
        payload.haulType = isLongHaul()
            ? "LONG_HAUL_TRIP"
            : isMediumHaul()
                ? "MEDIUM_HAUL_TRIP"
                : isShortHaul()
                    ? "SHORT_HAUL_TRIP"
                    : "UNKNOWN";
        payload.topAirlines = airlines.slice(0, 3).map(({ iataCode, name, count, minPrice }) => {
            return {
                iataCode,
                airlineName: name,
                offersCount: count,
                minPrice: pricingUtils.priceToNumber(minPrice)
            };
        });
        const directFlightsAggregation = stops.find(({ numberOfStops }) => numberOfStops === 0);
        payload.cheapestDirectAirlineName = directFlightsAggregation?.cheapestAirline?.name;
        if (summary?.stops) {
            payload.stopSummary = summary.stops.map(({ stopType, minPrice, count }) => {
                return {
                    stopType,
                    minPrice: minPrice ? pricingUtils.priceToNumber(minPrice) : undefined,
                    count
                };
            });
        }
        if (summary?.baggageByStops) {
            payload.baggageSummaryByStops = summary.baggageByStops.map(({ stopType, checkedInBaggageIncluded, handBaggageIncluded, handAndCheckedInBaggageIncluded }) => {
                return {
                    type: stopType,
                    checkedInBaggageIncluded,
                    handBaggageIncluded,
                    handAndCheckedInBaggageIncluded
                };
            });
        }
        if (summary?.layoverDuration) {
            payload.layoverDurationSummary = summary.layoverDuration;
        }
        if (summary?.layoverLocation) {
            payload.layoverLocationSummary = summary.layoverLocation;
        }
        if (summary?.departureTime) {
            payload.departureTimeSummary = summary?.departureTime;
        }
        return payload;
    }, [
        adults,
        airlines,
        cabinClass,
        children,
        isLongHaul,
        isMediumHaul,
        isShortHaul,
        requestPayloadV2,
        searchSegments,
        stops,
        summary,
        type
    ]);
    const requestPayload = useMemo(() => {
        return endpointVersion === "v3" ? requestPayloadV3 : requestPayloadV2;
    }, [endpointVersion, requestPayloadV3, requestPayloadV2]);
    const shouldDryRunV3 = useMemo(() => {
        return endpointVersion === "v2" && isFeatureRunningClientSide("FLIGHTS_SEARCH_SUMMARY_V3_DRY_RUN");
    }, [endpointVersion]);
    useEffect(() => {
        if (searchFetchStatus === "loading") {
            dispatch(searchAiSummaryActions.fetch());
        }
        else if (searchFetchStatus === "failed") {
            dispatch(searchAiSummaryActions.fetchError({ message: "Failed to fetch search results" }));
        }
        else if (searchFetchStatus === "success") {
            if (requestPayload.stops.length === 0 || requestPayload.passengers === 0) {
                dispatch(searchAiSummaryActions.fetchError({ message: "Empty search results" }));
            }
            else {
                dispatch(searchAiSummaryActions.fetch());
                clientFetch(`/api/search/ai-summary/${endpointVersion}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "X-Flights-Context-Name": contextName
                    },
                    body: JSON.stringify(requestPayload)
                })
                    .then((data) => {
                    dispatch(searchAiSummaryActions.fetchSuccess(data));
                })
                    .catch((error) => {
                    dispatch(searchAiSummaryActions.fetchError(error));
                });
                if (shouldDryRunV3) {
                    clientFetch("/api/search/ai-summary/v3", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "X-Flights-Context-Name": contextName
                        },
                        body: JSON.stringify(requestPayloadV3)
                    }).catch(() => {
                    });
                }
            }
        }
    }, [searchFetchStatus, JSON.stringify(requestPayload), JSON.stringify(requestPayloadV3), shouldDryRunV3]);
};
const useSearchFetchStatus = () => {
    const bound = useGetSearchResultsBound();
    const searchRegularFetchStatus = useSelector(getSearchFetchStatus);
    const searchOutboundFetchStatus = useSelector(getSearchOutboundFetchStatus);
    const searchInboundFetchStatus = useSelector(getSearchInboundFetchStatus);
    const searchFetchStatus = useMemo(() => {
        if (bound === "outbound") {
            return searchOutboundFetchStatus;
        }
        else if (bound === "inbound") {
            return searchInboundFetchStatus;
        }
        else {
            return searchRegularFetchStatus;
        }
    }, [bound, searchInboundFetchStatus, searchOutboundFetchStatus, searchRegularFetchStatus]);
    return searchFetchStatus;
};
export default useAiSummaryController;
